import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonRow,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';

export type SupportRequestStatus = 'new' | 'in_progress' | 'awaiting_user' | 'resolved' | 'closed';
export type SupportRequestAssignmentFilter = 'assigned' | 'unassigned';
export type SupportRequestUserTypeFilter = 'registered' | 'guest';
export type SupportRequestSlaFilter = 'breached' | 'warning';
export type SupportRequestSortMode = 'newest' | 'oldest' | 'sla' | 'unread';

export interface SupportRequestFilterData {
  status: SupportRequestStatus[];
  assignment: SupportRequestAssignmentFilter[];
  userType: SupportRequestUserTypeFilter[];
  sla: SupportRequestSlaFilter[];
  starredOnly: boolean;
  unreadOnly: boolean;
  sortMode: SupportRequestSortMode;
}

@Component({
  selector: 'app-support-request-filter-modal',
  templateUrl: './support-request-filter-modal.component.html',
  styleUrls: ['./support-request-filter-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonCheckbox,
    IonIcon,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonRadioGroup,
    IonRadio,
    IonFooter,
    IonGrid,
    IonRow,
    IonCol,
  ],
})
export class SupportRequestFilterModalComponent implements OnInit {
  private modalCtrl: ModalController = inject(ModalController);
  @Input() filters?: Partial<SupportRequestFilterData> | null;

  public readonly statusOptions: Array<{ value: SupportRequestStatus; label: string; checked: boolean }> = [
    { value: 'new', label: 'New', checked: false },
    { value: 'in_progress', label: 'In Progress', checked: false },
    { value: 'awaiting_user', label: 'Awaiting User', checked: false },
    { value: 'resolved', label: 'Resolved', checked: false },
    { value: 'closed', label: 'Closed', checked: false },
  ];

  public readonly assignmentOptions: Array<{ value: SupportRequestAssignmentFilter; label: string; checked: boolean }> =
    [
      { value: 'unassigned', label: 'Unassigned', checked: false },
      { value: 'assigned', label: 'Assigned', checked: false },
    ];

  public readonly userTypeOptions: Array<{ value: SupportRequestUserTypeFilter; label: string; checked: boolean }> = [
    { value: 'registered', label: 'Registered User', checked: false },
    { value: 'guest', label: 'Guest', checked: false },
  ];

  public readonly slaOptions: Array<{ value: SupportRequestSlaFilter; label: string; checked: boolean }> = [
    { value: 'breached', label: 'SLA Breached', checked: false },
    { value: 'warning', label: 'SLA Warning', checked: false },
  ];

  public starredOnly = false;
  public unreadOnly = false;
  public sortMode: SupportRequestSortMode = 'newest';

  ngOnInit() {
    this.applyInitialSelections();
  }

  applyFilters() {
    this.modalCtrl.dismiss({
      filters: {
        status: this.selectedValues(this.statusOptions),
        assignment: this.selectedValues(this.assignmentOptions),
        userType: this.selectedValues(this.userTypeOptions),
        sla: this.selectedValues(this.slaOptions),
        starredOnly: this.starredOnly,
        unreadOnly: this.unreadOnly,
        sortMode: this.sortMode,
      },
    });
  }

  public updateOption(option: { checked: boolean }, event: CustomEvent) {
    option.checked = Boolean(event.detail.checked);
  }

  public updateFlag(field: 'starredOnly' | 'unreadOnly', event: CustomEvent) {
    this[field] = Boolean(event.detail.checked);
  }

  clearFilters() {
    this.modalCtrl.dismiss({ clear: true });
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  private selectedValues<T extends string>(options: Array<{ value: T; checked: boolean }>): T[] {
    return options.filter((option) => option.checked).map((option) => option.value);
  }

  private applyInitialSelections() {
    const filters = this.filters;
    if (!filters) return;

    this.setCheckedValues(this.statusOptions, filters.status);
    this.setCheckedValues(this.assignmentOptions, filters.assignment);
    this.setCheckedValues(this.userTypeOptions, filters.userType);
    this.setCheckedValues(this.slaOptions, filters.sla);
    this.starredOnly = Boolean(filters.starredOnly);
    this.unreadOnly = Boolean(filters.unreadOnly);
    this.sortMode = filters.sortMode || 'newest';
  }

  private setCheckedValues<T extends string>(options: Array<{ value: T; checked: boolean }>, values?: T[]) {
    const selected = new Set(values || []);
    options.forEach((option) => {
      option.checked = selected.has(option.value);
    });
  }
}
